import { Component, computed, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { RouterModule } from '@angular/router';
import { BeaconQueryFilterDto } from '../../dto/beacon-query-filter.dto';
import { BeaconProjectionDto } from '../../dto/beacon-projection.dto';
import { BeaconForm } from '../beacon-form/beacon-form';

@Component({
  selector: 'app-beacon-table',
  imports: [RouterModule, BeaconForm],
  templateUrl: './beacon-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './beacon-table.component.scss',
})
export class BeaconTableComponent {
  isLoading = input<boolean>(false);
  beacons = input<BeaconProjectionDto[]>([]);
  currentFilters = input<BeaconQueryFilterDto>({ page: 0, pageSize: 100 });
  totalRecords = input<number>(0);
  first = input<number>(0);
  rows = input<number>(100);
  filters = output<BeaconQueryFilterDto>();
  selectBeacon = output<BeaconProjectionDto>();
  beaconUpdated = output<void>();

  showEditModal = signal(false);
  editingBeacon = signal<BeaconProjectionDto | null>(null);

  currentPageDisplay = computed(() => {
    const total = this.totalRecords();
    if (total === 0) return { first: 0, last: 0 };
    const f = this.first() + 1;
    const l = Math.min(this.first() + this.rows(), total);
    return { first: f, last: l };
  });

  rowsPerPageOptions = computed(() => {
    const currentRows = this.rows();
    const options = [5, 10, 20, 50, 100];
    if (!options.includes(currentRows)) {
      return [...options, currentRows].sort((a, b) => a - b);
    }
    return options;
  });

  onSelectBeacon(selectedBeacon: BeaconProjectionDto) {
    this.selectBeacon.emit(selectedBeacon);
  }

  onEditBeacon(beacon: BeaconProjectionDto): void {
    this.editingBeacon.set(beacon);
    this.showEditModal.set(true);
  }

  onModalClose(): void {
    this.showEditModal.set(false);
    this.editingBeacon.set(null);
  }

  onBeaconUpdated(): void {
    this.showEditModal.set(false);
    this.editingBeacon.set(null);
    this.beaconUpdated.emit();
  }

  onPrevPage() {
    const page = this.first() / this.rows() - 1;
    this.filters.emit({ ...this.currentFilters(), page, pageSize: this.rows() });
  }

  onNextPage() {
    const page = this.first() / this.rows() + 1;
    this.filters.emit({ ...this.currentFilters(), page, pageSize: this.rows() });
  }

  onRowsPerPageChange(event: Event) {
    const select = event.target as HTMLSelectElement;
    const pageSize = Number(select.value);
    this.filters.emit({ ...this.currentFilters(), page: 0, pageSize });
  }

  formatDate(date: Date | string): string {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  }

  getTimeDiffCategory(reportDate: Date, consultedAt: Date): 'minutes' | 'hours' | 'days' {
    const diffMs = new Date(consultedAt).getTime() - new Date(reportDate).getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    if (diffMinutes < 60) return 'minutes';
    if (Math.floor(diffMinutes / 60) < 24) return 'hours';
    return 'days';
  }

  getRowClass(beacon: BeaconProjectionDto): string {
    const cat = this.getTimeDiffCategory(beacon.reportDate, beacon.consultedAt);
    const map = { minutes: 'border-l-green-500', hours: 'border-l-orange-500', days: 'border-l-red-500' };
    return `border-l-4 ${map[cat]}`;
  }

  getGoogleMapsUrl(beacon: BeaconProjectionDto): string {
    if (beacon.latitude == null || beacon.longitude == null) return '';
    return `https://www.google.com/maps?q=${beacon.latitude},${beacon.longitude}`;
  }

  getTimeDifference(reportDate: Date, consultedAt: Date): string {
    const diffMs = new Date(consultedAt).getTime() - new Date(reportDate).getTime();
    const minutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d`;
    if (hours > 0) return `${hours}h`;
    if (minutes > 0) return `${minutes}m`;
    return `${Math.floor(diffMs / 1000)}s`;
  }
}
